"use client";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/app/admin/_common/ui/dialog";
import { Input } from "@/app/admin/_common/ui/input";
import { Label } from "@/app/admin/_common/ui/label";
import { Checkbox } from "@/app/admin/_common/ui/checkbox";
import CuoButton from "@/app/admin/_common/CuoButton";
import { Mail } from "lucide-react";
import { toast } from "@/app/_utils/toast";
import { getCsrfToken } from "@/app/_utils/csrf";

export default function EmailDialog({
  open,
  onClose,
  order,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  order: any;
  onSuccess?: () => void;
}) {
  const [email, setEmail] = useState("");
  const [replace, setReplace] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!order) return null;

  function handleClose() {
    setEmail("");
    setReplace(false);
    onClose();
  }

  async function handleSend() {
    setLoading(true);
    try {
      const res = await fetch(`${import.meta.env.VITE_API_BASE_URL}show/order/`, {
        method: "PUT",
        credentials: "include",
        body: JSON.stringify({ action: "email", orderId: order.id, email, replace }),
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCsrfToken(),
        },
      });
      if (res.status === 200) {
        toast.success("Confirmation resent");
        handleClose();
        if (onSuccess) onSuccess();
        else window.location.reload();
      } else {
        toast.error("An error has occurred");
      }
    } catch (e) {
      toast.error("An error has occurred");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !loading && handleClose()}>
      <DialogContent className="max-w-[420px]">
        <DialogHeader>
          <DialogTitle>
            <span className="inline-flex items-center gap-2 align-middle">
              <Mail className="size-4 flex-none text-muted-foreground" />
              Resend confirmation
            </span>
          </DialogTitle>
          <DialogDescription>Resend the ticket confirmation to another email.</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label htmlFor="resend-email">Guest email</Label>
            <Input
              id="resend-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="guest@gmail.com"
            />
          </div>
          <label className="flex items-center gap-2 text-[13px] text-foreground">
            <Checkbox checked={replace} onCheckedChange={(c) => setReplace(Boolean(c))} />
            Update guest email
          </label>
        </div>

        <DialogFooter>
          <CuoButton variant="outline" label="Cancel" onClick={handleClose} disabled={loading} />
          <CuoButton
            disableOnAsync={loading}
            variant="default"
            label="Send"
            onClick={handleSend}
            disabled={!email}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
